export const AUTH_MESSAGE_KEY = 'auth_message';
export const SESSION_EXPIRED_REASON = 'session-expired';
export const ACCESS_TOKEN_KEY = 'access';
export const REFRESH_TOKEN_KEY = 'refresh';
export const USER_KEY = 'user';
export const COMPANY_ID_KEY = 'company_id';
const LEGACY_ACCESS_TOKEN_KEY = 'token';
const LEGACY_REFRESH_TOKEN_KEY = 'refreshToken';

const INVALID_STORED_VALUES = new Set(['', 'null', 'undefined', 'false']);

export const hasUsableStoredValue = (value) => {
  if (value === null || value === undefined) return false;
  return !INVALID_STORED_VALUES.has(String(value).trim().toLowerCase());
};

const getStoredValue = (...keys) => {
  for (const key of keys) {
    const value = localStorage.getItem(key);
    if (hasUsableStoredValue(value)) {
      return value;
    }
  }
  return null;
};

export const getStoredAccessToken = () => getStoredValue(ACCESS_TOKEN_KEY, LEGACY_ACCESS_TOKEN_KEY);
export const getStoredRefreshToken = () => getStoredValue(REFRESH_TOKEN_KEY, LEGACY_REFRESH_TOKEN_KEY);
export const getStoredCompanyId = () => getStoredValue(COMPANY_ID_KEY);
export const getStoredUser = () => {
  const rawUser = getStoredValue(USER_KEY);
  if (!rawUser) return null;

  try {
    return normalizeAuthProfile(JSON.parse(rawUser), getStoredCompanyId());
  } catch {
    return null;
  }
};

export const getEffectiveCompanyId = (user, fallbackCompanyId = '1') => (
  getProfileCompanyId(user, getStoredCompanyId() || fallbackCompanyId) || fallbackCompanyId
);

export const getProfileCompanyId = (profile, fallbackCompanyId = null) => (
  profile?.company_id
  || profile?.company?.id
  || (typeof profile?.company === 'number' ? profile.company : null)
  || fallbackCompanyId
);

export const normalizeAuthProfile = (profile, fallbackCompanyId = null) => {
  if (!profile || typeof profile !== 'object') return null;

  const directProfile = profile.user && typeof profile.user === 'object'
    ? { ...profile.user, ...profile }
    : { ...profile };
  delete directProfile.user;

  const companyId = getProfileCompanyId(directProfile, fallbackCompanyId);
  const company = directProfile.company && typeof directProfile.company === 'object'
    ? { ...directProfile.company }
    : {};

  if (companyId && !company.id) {
    company.id = companyId;
  }

  return {
    ...directProfile,
    company_id: companyId || directProfile.company_id || null,
    company: Object.keys(company).length ? company : directProfile.company,
  };
};

export const storeAuthSession = ({
  accessToken,
  refreshToken,
  user,
  companyId,
}) => {
  const normalizedUser = normalizeAuthProfile(user, companyId);
  const resolvedCompanyId = companyId || getProfileCompanyId(normalizedUser);

  if (accessToken) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
  }

  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
  }

  if (normalizedUser) {
    localStorage.setItem(USER_KEY, JSON.stringify(normalizedUser));
  }

  if (resolvedCompanyId !== undefined && resolvedCompanyId !== null && resolvedCompanyId !== '') {
    localStorage.setItem(COMPANY_ID_KEY, String(resolvedCompanyId));
  }
};

export const getAuthHeaders = (token = getStoredAccessToken()) => (
  hasUsableStoredValue(token) ? { Authorization: `Bearer ${token}` } : {}
);

export const clearStoredAuth = ({ preserveCompanyId = true } = {}) => {
  const companyId = preserveCompanyId ? getStoredCompanyId() : null;

  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
  localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);

  if (!preserveCompanyId) {
    localStorage.removeItem(COMPANY_ID_KEY);
  } else if (companyId) {
    localStorage.setItem(COMPANY_ID_KEY, companyId);
  }
};

export const setAuthMessage = (message) => {
  if (!message) return;
  sessionStorage.setItem(AUTH_MESSAGE_KEY, message);
};

export const clearAuthMessage = () => {
  sessionStorage.removeItem(AUTH_MESSAGE_KEY);
};

export const consumeAuthMessage = () => {
  const message = sessionStorage.getItem(AUTH_MESSAGE_KEY) || '';
  if (message) {
    clearAuthMessage();
  }
  return message;
};

export const getLoginPath = ({
  companyId,
  reason,
} = {}) => {
  const effectiveCompanyId = companyId || getStoredCompanyId() || '1';
  const params = new URLSearchParams({ companyId: String(effectiveCompanyId) });

  if (reason) {
    params.set('reason', reason);
  }

  return `/login?${params.toString()}`;
};

export const isJwtSessionError = (value) => {
  const normalized = String(value || '').toLowerCase();
  return (
    normalized.includes('given token not valid') ||
    normalized.includes('token not valid for any token type') ||
    normalized.includes('token is invalid or expired') ||
    normalized.includes('token is expired') ||
    normalized.includes('authorization header') ||
    normalized.includes('not_authenticated')
  );
};

export const sanitizeBackendMessage = (value, fallback) => {
  const text = String(value || '').trim();
  if (!text) return fallback;
  if (text.startsWith('<!DOCTYPE html') || text.startsWith('<html')) {
    return fallback;
  }
  return text;
};

export const getApiErrorMessage = (err, {
  fallback = 'Something went wrong. Please try again.',
  sessionFallback = 'Session expired. Please log in again.',
  permissionFallback = 'You do not have permission or your company subscription is inactive.',
  notFoundFallback = fallback,
  serverFallback = 'Server error. Please try again later.',
} = {}) => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const errorCode = typeof data === 'object' && data ? data.code || '' : '';
  const rawMessage = typeof data === 'string'
    ? data
    : data?.error || data?.detail || data?.message || '';

  if (errorCode === 'blocked_harmful_upload' || errorCode === 'blocked_suspicious_upload') {
    return 'This file was blocked because it appears harmful.';
  }

  if (isJwtSessionError(rawMessage)) {
    return sessionFallback;
  }

  if (status === 403) {
    return permissionFallback;
  }

  if (status === 404) {
    return sanitizeBackendMessage(rawMessage, notFoundFallback);
  }

  if (status >= 500) {
    return sanitizeBackendMessage(rawMessage, serverFallback);
  }

  return sanitizeBackendMessage(rawMessage, fallback);
};

export const handleExpiredSession = ({
  navigate,
  companyId,
  message = 'Session expired. Please log in again.',
  preserveCompanyId = true,
  showMessage = true,
} = {}) => {
  clearStoredAuth({ preserveCompanyId });
  if (showMessage) {
    setAuthMessage(message);
  } else {
    clearAuthMessage();
  }

  if (!navigate) return;

  navigate(getLoginPath({
    companyId,
    reason: showMessage ? SESSION_EXPIRED_REASON : undefined,
  }), { replace: true });
};
